<script lang="ts">
import { AuthPopover, UserRoles } from '@onereach/auth-ui-module';
import { ServiceNavigationHeader, LocalizedServiceTranslations, Config } from '@onereach/service-navigation';
import { OrNotificationV3, useResponsive } from '@onereach/ui-components';
import { useVModel } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { PropType, defineComponent, computed } from 'vue';
import { useRoute } from 'vue-router';

import { useCcpStore } from '@/stores';

import { LoadingView } from '../LoadingView';
import { PageHeader } from '../PageHeader';
import { Sidebar } from '../Sidebar';

import CustomerWrapper from './CustomerWrapper.vue';

export default defineComponent({
  components: {
    ServiceNavigationHeader,
    AuthPopover,
    LoadingView,
    PageHeader,
    // TODO: CodeStyle, rename to NavSidebar
    Sidebar,
    OrNotificationV3,
    CustomerWrapper,
  },
  props: {
    token: {
      type: String,
      required: false,
      default: undefined,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    config: {
      type: Object as PropType<Config>,
      required: true,
    },
    serviceTranslations: {
      type: Object as PropType<LocalizedServiceTranslations>,
      required: false,
      default: null,
    },
    accountId: {
      type: String,
      required: false,
      default: null,
    },
    userRole: {
      type: String as PropType<UserRoles>,
      default: undefined,
    },
    showError: {
      type: Boolean,
      required: false,
      default: false,
    },
    errorText: {
      type: String,
      required: false,
      default: null,
    },
    internalMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    customAppName: {
      type: String,
      required: false,
      default: null,
    },
  },
  emits: ['update:showError'],
  setup(props, { emit }) {
    const isErrorShown = useVModel(props, 'showError', emit);

    const { isMobile } = useResponsive();
    const route = useRoute();

    const pageTitle = computed(() => route?.meta?.title as string || '');
    const isSafari = typeof navigator !== 'undefined'
      && /Safari/.test(navigator.userAgent)
      && !/Chrome|Chromium|CriOS|FxiOS|EdgiOS|OPiOS|Android/.test(navigator.userAgent);

    const pageStyles = computed(() => ([
      ...(isMobile.value ? ['py-lg', 'px-md'] : ['p-xl', 'pb-md']),
    ]));

    const { selectedCustomer, customersList } = storeToRefs(useCcpStore());

    const customersOptions = computed(() => customersList.value.map(customer => {
      return {
        label: customer.name,
        value: customer.id,
      };
    }));

    return {
      isErrorShown,
      pageTitle,
      pageStyles,
      isMobile,
      isSafari,
      selectedCustomer,
      customersOptions,
    };
  },
});


</script>

<template>
  <div class="h-full layout-column">
    <service-navigation-header
      :config="config"
      :service-translations="serviceTranslations"
      :token="token"
      :account-id="accountId"
      :user-role="userRole"
    >
      <template
        v-if="customAppName"
        #logo
      >
        <span class="typography-headline-2 truncate text-white">
          {{ customAppName }}
        </span>
      </template>
      <template #account>
        <auth-popover
          :token="token"
          :dark-mode-options="{
            attribute: 'data-theme',
            valueDark: 'dark',
          }"
        />
      </template>
    </service-navigation-header>
    <OrNotificationV3
      v-if="isErrorShown"
      variant="error"
      class="error-notification absolute z-[100] w-[calc(100%-(2*theme(spacing.3xl)))] m-3xl"
      @close="isErrorShown=false"
    >
      {{ errorText }}
    </OrNotificationV3>
    <LoadingView v-if="loading" />
    <div
      v-else
      id="app-root"
      class="grow flex min-h-0 bg-surface-1 dark:bg-surface-1-dark"
    >
      <Sidebar :internal-mode="internalMode" />
      <div
        class="w-full grow overflow-y-hidden layout-column gap-md"
        :class="pageStyles"
      >
        <PageHeader
          v-if="pageTitle || isMobile"
          :title="pageTitle"
        />
        <CustomerWrapper
          v-model="selectedCustomer"
          :customers-options="customersOptions"
          :internal-mode="internalMode"
        >
          <div
            class="overflow-auto"
            :class="{ 'safari-page-content': isSafari }"
          >
            <slot />
          </div>
        </CustomerWrapper>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.safari-page-content {
  flex: 1 1 0%;
  min-height: 0;
}

.safari-page-content > .layout-column.h-full {
  min-height: 0;
}

.safari-page-content > .layout-column.h-full > .layout-column {
  flex: 1 1 0%;
  min-height: 0;
}

.safari-page-content .safari-data-grid-wrapper {
  min-height: 0;
}
</style>
